import React, { useState, useEffect, Suspense, useContext } from 'react';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { CssBaseline, ThemeProvider } from '@mui/material';

import { useOnlineStatus } from '../src/utility/useOnlineStatus';
import Loader from './components/FormsUI/Loader/index';
import { MenuAccessProvider, MenuAccessContext } from './utility/getRoles';
import CheckPermissions from './utility/checkPermissions';
import { RequireAuth } from './utility/RequireAuth';
import { AuthProvider, useAuth } from './utility/auth';
import AuthSetup from './utility/authSetup';
import { ColorModeContext, useMode } from './theme';
import Login from './pages/login';
import { injectAuthHelper } from './common/apiClient';
import SidebarLayout from './components/global/SidebarLayout';
import Chatbot from './components/ChatBot/Chatbot.jsx';


const Notfound = React.lazy(() => import('./pages/notFound'));
const { mapComponents } = require('./routeConfig.js');

function App() {
  const isOnline = useOnlineStatus();
  const [theme, colorMode] = useMode();

  return (
    <AuthProvider>
      <AuthSetup />
      {!isOnline && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'red',
            color: 'white',
            padding: '10px',
            textAlign: 'center',
            zIndex: 9999,
          }}
        >
          ⚠️ You are offline. Please check your internet connection.
        </div>
      )}
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Suspense fallback={<Loader />}>
            <MenuAccessProvider>
              <InnerApp />
            </MenuAccessProvider>
          </Suspense>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </AuthProvider>
  );
}

function InnerApp() {
  const { menuAccess } = useContext(MenuAccessContext);
  const { setUserFromToken } = useAuth();
  const navigate = useNavigate();
  const [accessRoutes, setAccessRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);

  useEffect(() => {
    // Wait for menuAccess to be defined before processing
    if (menuAccess === undefined) {
      setLoadingRoutes(false);
      return;
    }

    if (!menuAccess || menuAccess.length === 0) {
      setLoadingRoutes(false);
      return;
    }

    try {
      const flatRoutes = menuAccess.flatMap((menuItem) => {
        const routes = [];

        if (menuItem.path && menuItem.menuName) {
          routes.push({
            path: menuItem.path.startsWith('/') ? menuItem.path : `/${menuItem.path}`,
            componentName: menuItem.menuName,
          });
        }

        if (Array.isArray(menuItem.submenus)) {
          const subRoutes = menuItem.submenus
            .filter((sub) => sub.path && sub.submenuName)
            .map((sub) => ({
              path: sub.path.startsWith('/') ? sub.path : `/${sub.path}`,
              componentName: sub.submenuName,
            }));
          routes.push(...subRoutes);
        }

        return routes;
      });

      setAccessRoutes(flatRoutes);
      setLoadingRoutes(false);
    } catch (e) {
      console.error('Failed to process menuAccess:', e);
      setLoadingRoutes(false);
    }
  }, [menuAccess]);

  useEffect(() => {
    injectAuthHelper({
      setUserFromToken,
      redirectToLogin: () => navigate('/login', { state: { data: 'expired' } }),
    });
  }, [setUserFromToken, navigate]);

  if (loadingRoutes) {
    return <Loader />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to="/login" replace />} />

      <Route element={<SidebarLayout />}>
        {accessRoutes.map(({ path, componentName }) => {
          const Component = mapComponents[componentName];
          return (
            Component && (
              <Route
                key={path}
                path={path}
                element={
                  <RequireAuth>
                    <Suspense fallback={<div>Loading, please wait...</div>}>
                      <CheckPermissions
                        requiredAccess={componentName}
                        element={<Component />}
                      />
                      <Chatbot />
                    </Suspense>
                  </RequireAuth>
                }
              />
            )
          );
        })}
      </Route>

      {/* Only show NotFound when routes are confirmed loaded */}
      <Route path="*" element={<Notfound />} />
    </Routes>
  );
}

export default App;
